import {createContext, use, useCallback, useEffect, useRef, useState} from 'react';

import {CloseIcon} from '../partials/icons';
import {useTranslation} from '../../../hooks/use-translation';

import './toaster.scss';

export type Toast = {
    content: string;
    header?: string;
    /** success by default; a danger toast is an alert for screen readers */
    variant?: 'success' | 'warning' | 'danger';
    /** in milliseconds, 4 seconds by default */
    dismissTimer?: number;
};

type ShownToast = Toast & {id: number};

/** older toasts make way for new ones */
const maxToasts = 8;

const ToastContext = createContext<(toast: Toast) => void>(() => undefined);

/** Show a toast, from anywhere under the `ToastProvider`: `toaster({content, variant: 'danger'})`. */
export const useToaster = (): ((toast: Toast) => void) => use(ToastContext);

/**
 * The toasts, newest on top, in the top right corner. The list is a manual popover while it holds any, so it lies in the
 * top layer, over an open dialog (`showModal()` puts a dialog there as well, over whatever was there before it).
 */
export const ToastProvider = ({children}: {children: React.ReactNode}): React.JSX.Element => {
    const t = useTranslation();
    const listRef = useRef<HTMLOListElement>(null);
    const lastIdRef = useRef(0);
    const [toasts, setToasts] = useState<ShownToast[]>([]);
    const newest = toasts[0]?.id;

    const remove = useCallback((id: number) => setToasts(shown => shown.filter(toast => toast.id !== id)), []);

    const add = useCallback(
        (toast: Toast) => {
            const id = ++lastIdRef.current;

            setToasts(shown => [{...toast, id}, ...shown].slice(0, maxToasts));
            window.setTimeout(() => remove(id), toast.dismissTimer ?? 4000);
        },
        [remove],
    );

    // a new toast shows the list again, on top of a dialog opened since (also one opened in the same render: the effects
    // of the dialog, a descendant, run before this one)
    useEffect(() => {
        const list = listRef.current!;

        if (list.matches(':popover-open')) {
            list.hidePopover();
        }

        if (undefined !== newest) {
            list.showPopover();
        }
    }, [newest]);

    return (
        <ToastContext value={add}>
            {children}
            <ol ref={listRef} className="toaster" popover="manual">
                {toasts.map(({id, header, content, variant = 'success'}) => (
                    <li key={id} role={'danger' === variant ? 'alert' : 'status'} data-variant={variant}>
                        {header && <strong>{header}</strong>}
                        {content}
                        <button type="button" aria-label={t('common.close')} onClick={() => remove(id)}>
                            <CloseIcon />
                        </button>
                    </li>
                ))}
            </ol>
        </ToastContext>
    );
};
